import React, { useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, MicIcon, ImageIcon, PaperclipIcon, ArrowUpIcon, CarIcon, HomeIcon, BriefcaseIcon, TagIcon, TruckIcon, SmartphoneIcon, HistoryIcon, ChevronRightIcon, XIcon, PlusIcon, TrendingDownIcon, ThumbsUpIcon, AlertTriangleIcon, WrenchIcon, ShoppingBagIcon, GraduationCapIcon, UtensilsIcon, ShirtIcon, SprayCanIcon, SofaIcon, PlaneIcon, UserIcon, LaptopIcon } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { AIResponse } from '../components/AIResponse';
import { ListingCard, ListingCardProps } from '../components/ListingCard';
import { ListingCarousel } from '../components/ListingCarousel';
import { CategoryRibbon } from '../components/CategoryRibbon';
import { CreateShopButton } from '../components/CreateShopButton';
import { SendButton } from '../components/SendButton';
import { ComposerRing } from '../components/ComposerRing';
import { foodListings, foodNarrowed } from '../data/food';
import { apparelListings, apparelNarrowed, perfumeListings, perfumeNarrowed } from '../data/retail';
import { furnitureListings, furnitureNarrowed } from '../data/furniture';
import { electronicsListings, electronicsNarrowed } from '../data/electronics';
import { LiveSearchResponse, LiveSearchStatus } from '../components/search/LiveSearchResponse';
import { AssistantMark } from '../components/search/AssistantMark';
import { TypingDots } from '../components/search/TypingDots';
import { SearchSpeed, SEARCH_SPEED_MS, narrateSearch, isOpenQuestion } from '../utils/searchNarration';
import { inventoryItems } from '../data/inventory';
import { toListingCard } from '../utils/inventory';
import { BrandStore, BrandMenuItem, findBrand } from '../data/burgerKing';
import { BrandCategoryCards } from '../components/food/BrandCategoryCards';
import { useBusiness, BusinessType } from '../components/BusinessContext';
import { useAuth } from '../components/AuthContext';
// --- Seller intent detection ---
type SellIntent = {
  tier: 'individual' | 'business' | 'enterprise';
  type: BusinessType;
};
const detectSellIntent = (text: string): SellIntent | null => {
  const t = text.toLowerCase();
  const sellish = /\b(sell|selling|list|listing|post|inventory|stock|dealer|agency|recruit|hire|hiring|jobs?|shop|store|business|merchant|products?|services?|manage (leads|inquiries|properties|listings))\b/.test(t);
  if (!sellish) return null;
  // Business type
  let type: BusinessType = 'generic';
  if (/\b(car|cars|vehicle|vehicles|auto|motors?|dealership|dealer)\b/.test(t)) type = 'car';else if (/\b(real estate|property|properties|villa|apartment|agency|broker)\b/.test(t)) type = 'realestate';else if (/\b(recruit|hiring|hire|jobs?|candidates?|talent)\b/.test(t)) type = 'recruitment';else if (/\b(service|services|provider|cleaning|moving|maintenance)\b/.test(t)) type = 'service';else if (/\b(products?|furniture|electronics|goods|items?|inventory|shop|store)\b/.test(t)) type = 'product';
  // Enterprise signals: dealer / agency / recruiter / company / multiple
  const enterprise = /\b(dealer|dealership|agency|recruiter|recruitment|company|enterprise|fleet|represent)\b/.test(t);
  // Business signals: ongoing selling, inventory, multiple, manage
  const business = /\b(business|shop|store|inventory|stock|products?|services?|multiple|many|several|bulk|manage (leads|inquiries|listings))\b/.test(t);
  // Individual: "sell my <single item>"
  const individual = /\bsell(ing)?\s+(my|a|an|this|one)\s+\w+/.test(t);
  let tier: SellIntent['tier'] = 'individual';
  if (enterprise) tier = 'enterprise';else if (business) tier = 'business';else if (individual) tier = 'individual';else tier = 'business'; // generic seller phrasing leans business
  return {
    tier,
    type
  };
};
const BUSINESS_LABEL_INLINE: Record<BusinessType, string> = {
  car: 'vehicle inventory',
  realestate: 'property listings',
  recruitment: 'job postings and candidates',
  service: 'services',
  product: 'product inventory',
  generic: 'listings'
};
// One entry per kind of result Talab can return, so every card design is one
// tap away from the empty state.
const suggestedPrompts = [
// Progressive-search demos — one per sector, each a full ~10s search.
{
  text: 'Show me 2-bedroom apartments in Dubai Marina',
  icon: HomeIcon
}, {
  text: 'Show me black dresses in medium',
  icon: ShirtIcon
}, {
  text: 'I need an SUV tomorrow for 3 days',
  icon: CarIcon
}, {
  text: 'Show me woody perfumes under AED 300',
  icon: SprayCanIcon
}, {
  text: 'Show me gaming laptops under AED 5,000',
  icon: LaptopIcon
}, {
  text: 'What do you have available?',
  icon: SparklesIcon
}, {
  text: 'I want a burger near me',
  icon: UtensilsIcon
}, {
  text: 'Show me the Burger King menu',
  icon: UtensilsIcon
}, {
  text: 'Find me an oud perfume',
  icon: SprayCanIcon
}, {
  text: 'Show me apparel in my size',
  icon: ShirtIcon
}, {
  text: 'Rent an SUV for a month',
  icon: CarIcon
}, {
  text: 'Find me a Prado under AED 90,000',
  icon: CarIcon
}, {
  text: 'I need a sofa for my living room',
  icon: SofaIcon
}, {
  text: 'Apartments to rent in Marina',
  icon: HomeIcon
}, {
  text: "Need a villa near my children's school",
  icon: HomeIcon
}];
const categoryRibbonItems = [{
  label: 'Cars',
  icon: CarIcon,
  prompt: 'Find me a car'
}, {
  label: 'Properties',
  icon: HomeIcon,
  prompt: 'Find me a property'
}, {
  label: 'Food',
  icon: UtensilsIcon,
  prompt: 'I want a burger near me'
}, {
  label: 'Apparel',
  icon: ShirtIcon,
  prompt: 'Show me apparel in my size'
}, {
  label: 'Perfumes',
  icon: SprayCanIcon,
  prompt: 'Find me an oud perfume'
}, {
  label: 'Furniture',
  icon: SofaIcon,
  prompt: 'I need a sofa for my living room'
}];
const recentSearches = ['Prado under AED 90,000', 'Villa near GEMS School', 'ERP Manager job in Dubai', 'iPhone 16 Pro price'];
// Mock data for search results
const mockInsights = [{
  title: 'Average Price',
  value: 'AED 84,000',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: 'Prado GXR 2018',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: '1'
}, {
  title: 'Lowest Mileage',
  value: 'Prado VX 2019',
  type: 'positive' as const,
  icon: SparklesIcon,
  targetId: '4'
}, {
  title: 'Risk Alerts',
  value: '4 imported detected',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
const propertyInsights = [{
  title: 'Average Price',
  value: 'AED 2.8M',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: '5BR Villa — Mudon',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: 'p3'
}, {
  title: 'Closest to School',
  value: 'Dubai Hills · 3 min',
  type: 'positive' as const,
  icon: HomeIcon,
  targetId: 'p5'
}, {
  title: 'Note',
  value: '2 listings under offer',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
type SearchCategory = 'cars' | 'car-rental' | 'property' | 'food' | 'apparel' | 'perfume' | 'furniture' | 'electronics';
const detectCategory = (text: string): SearchCategory => {
  const t = text.toLowerCase();
  // Open-ended "what do you have?" — answered from the demo fragrance house.
  if (isOpenQuestion(t)) return 'perfume';
  /* Vehicles are checked first and by whole word: "rent" alone used to send
     "rent an SUV" to villas, and "seat" in "7-seater" matched food's "eat". */
  const isVehicle = /\b(suvs?|4x4|4wd|cars?|sedans?|vehicles?|prado|patrol|land cruiser|range rover|jeep|pickups?|vans?|hatchback|coupe|convertible|\d-seater)\b/.test(t);
  if (isVehicle) {
    return /\b(rent|renting|rental|hire|lease|leasing|per day|daily|weekly|monthly|today|tomorrow|tonight|weekend|\d+\s*days?|for (a|one) (day|week|month))\b/.test(t) ? 'car-rental' : 'cars';
  }
  if (/\b(electronics?|gaming laptops?|laptops?|notebooks?|macbooks?|smartphones?|iphones?|phones?|tablets?|ipads?|headphones?|earbuds?|monitors?|tvs?|television|consoles?|playstation|xbox|smartwatch(es)?)\b/.test(t)) return 'electronics';
  const furnitureHints = ['furniture', 'sofa', 'couch', 'armchair', 'dining table', 'coffee table', 'bed frame', 'mattress', 'wardrobe', 'shelving', 'bookshelf', 'desk', 'dresser', 'living room', 'furnish'];
  if (furnitureHints.some(h => t.includes(h))) return 'furniture';
  const perfumeHints = ['perfume', 'fragrance', 'cologne', 'eau de', 'oud', 'attar', 'scent', 'bakhoor'];
  if (perfumeHints.some(h => t.includes(h))) return 'perfume';
  const apparelHints = ['apparel', 'clothing', 'clothes', 'fashion', 'shirt', 'dress', 'abaya', 'kandura', 'jacket', 'jeans', 'trousers', 'shoes', 'sneaker', 'outfit', 'wear', 'knit', 'suit'];
  if (apparelHints.some(h => t.includes(h))) return 'apparel';
  // Food is checked first — "burger" should never fall through to cars.
  const foodHints = ['burger', 'pizza', 'shawarma', 'sushi', 'biryani', 'pasta', 'salad', 'fries', 'sandwich', 'wings', 'dessert', 'cake', 'coffee', 'breakfast', 'lunch', 'dinner', 'eat', 'food', 'restaurant', 'takeaway', 'delivery near', 'hungry', 'meal', 'menu'];
  if (foodHints.some(h => t.includes(h))) return 'food';
  const propertyHints = ['villa', 'apartment', 'apt', 'home', 'house', 'property', 'bedroom', 'br ', 'townhouse', 'flat', 'rent', 'school', 'studio'];
  return propertyHints.some(h => t.includes(h)) ? 'property' : 'cars';
};
const CAR_IMG_A = 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_B = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_C = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=800';
// Full result set (label says 27; we show a representative slice in the carousel)
const mockListings = [{
  id: '1',
  title: 'Toyota Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 98,
  sellerType: 'Verified Business' as const
}, {
  id: '2',
  title: 'Toyota Prado TXL 2019',
  price: 'AED 89,000',
  location: 'Abu Shagara, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '85,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 94,
  sellerType: 'Individual' as const
}, {
  id: '3',
  title: 'Toyota Prado VXR 2017',
  price: 'AED 78,000',
  location: 'Motor City, Dubai',
  images: [CAR_IMG_C, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '145,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Black'
  }],
  matchScore: 88,
  sellerType: 'Dealer' as const
}, {
  id: '4',
  title: 'Toyota Prado VX 2019',
  price: 'AED 87,500',
  location: 'Mussafah, Abu Dhabi',
  images: [CAR_IMG_A, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '64,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 92,
  sellerType: 'Verified Business' as const
}, {
  id: '5',
  title: 'Toyota Prado GXR 2016',
  price: 'AED 69,000',
  location: 'Al Nahda, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2016'
  }, {
    label: 'Mileage',
    value: '168,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Grey'
  }],
  matchScore: 81,
  sellerType: 'Individual' as const
}, {
  id: '6',
  title: 'Toyota Prado TXL 2018',
  price: 'AED 84,900',
  location: 'Deira, Dubai',
  images: [CAR_IMG_C, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '98,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 90,
  sellerType: 'Dealer' as const
}, {
  id: '7',
  title: 'Toyota Prado VX 2018',
  price: 'AED 86,000',
  location: 'JVC, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '77,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Beige'
  }],
  matchScore: 89,
  sellerType: 'Verified Business' as const
}, {
  id: '8',
  title: 'Toyota Prado GXR 2017',
  price: 'AED 74,500',
  location: 'Al Ain',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '132,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 83,
  sellerType: 'Individual' as const
}];
// The 3 strong matches after the user narrows their request
const narrowedListings = [mockListings[3], mockListings[6], mockListings[0]];
/* Rental answers come from the rental fleet (priced per day), SUVs first,
   rather than from cars for sale. */
const rentalListings: ListingCardProps[] = [...inventoryItems].sort((a, b) => Number(b.category === 'SUVs') - Number(a.category === 'SUVs')).map(toListingCard);
const rentalNarrowed = rentalListings.slice(0, 3);
// ---- Real-estate (property) dataset ----
const HOME_IMG_A = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_B = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_C = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800';
const propertyListings: ListingCardProps[] = [{
  id: 'p1',
  title: '4BR Villa — Arabian Ranches',
  price: 'AED 3.2M',
  location: 'Arabian Ranches, Dubai',
  images: [HOME_IMG_A, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,400 sqft'
  }, {
    label: 'School',
    value: '5 min'
  }],
  matchScore: 97,
  sellerType: 'Verified Business' as const
}, {
  id: 'p2',
  title: '3BR Townhouse — Town Square',
  price: 'AED 1.9M',
  location: 'Town Square, Dubai',
  images: [HOME_IMG_B, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,100 sqft'
  }, {
    label: 'School',
    value: '8 min'
  }],
  matchScore: 93,
  sellerType: 'Dealer' as const
}, {
  id: 'p3',
  title: '5BR Villa — Mudon',
  price: 'AED 4.1M',
  location: 'Mudon, Dubai',
  images: [HOME_IMG_C, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '5 BR'
  }, {
    label: 'Bathrooms',
    value: '6 Bath'
  }, {
    label: 'Area',
    value: '4,200 sqft'
  }, {
    label: 'School',
    value: '4 min'
  }],
  matchScore: 95,
  sellerType: 'Verified Business' as const
}, {
  id: 'p4',
  title: '4BR Villa — The Springs',
  price: 'AED 2.8M',
  location: 'The Springs, Dubai',
  images: [HOME_IMG_A, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,900 sqft'
  }, {
    label: 'School',
    value: '6 min'
  }],
  matchScore: 91,
  sellerType: 'Individual' as const
}, {
  id: 'p5',
  title: '3BR Apartment — Dubai Hills',
  price: 'AED 2.3M',
  location: 'Dubai Hills Estate',
  images: [HOME_IMG_C, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '3 Bath'
  }, {
    label: 'Area',
    value: '1,800 sqft'
  }, {
    label: 'School',
    value: '3 min'
  }],
  matchScore: 89,
  sellerType: 'Dealer' as const
}, {
  id: 'p6',
  title: '4BR Villa — Reem Community',
  price: 'AED 2.6M',
  location: 'Reem, Dubai',
  images: [HOME_IMG_B, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,100 sqft'
  }, {
    label: 'School',
    value: '7 min'
  }],
  matchScore: 88,
  sellerType: 'Verified Business' as const
}];
const propertyNarrowed = [propertyListings[2], propertyListings[0], propertyListings[4]];
/* ---------- What each kind of search returns and says ---------- */
const listingsFor = (category: SearchCategory): ListingCardProps[] => category === 'electronics' ? electronicsListings : category === 'furniture' ? furnitureListings : category === 'perfume' ? perfumeListings : category === 'apparel' ? apparelListings : category === 'food' ? foodListings : category === 'property' ? propertyListings.map(l => ({
  ...l,
  sector: 'property' as const
})) : category === 'car-rental' ? rentalListings : mockListings;
const countLabelFor = (category: SearchCategory) => category === 'furniture' ? 'Showing top matches of 38' : category === 'perfume' ? 'Showing top matches of 32' : category === 'apparel' ? 'Showing top matches of 46' : category === 'food' ? 'Showing top matches of 24' : category === 'property' ? 'Showing top matches of 18' : category === 'car-rental' ? `Showing ${rentalListings.length} rental cars` : 'Showing top matches of 27';
const hintFor = (category: SearchCategory) => category === 'furniture' ? 'Check the dimensions and pick a finish →' : category === 'perfume' ? 'Pick a bottle size to add it to your bag →' : category === 'apparel' ? 'Choose a colour and size on any piece →' : category === 'food' ? 'Tap "Choose options" to size it and pick extras →' : category === 'property' ? 'Tap "Ask AI" on a home to ask about it →' : 'Tap "Ask AI" on a car to ask about it →';
const searchContentFor = (category: SearchCategory) => category === 'furniture' ? 'I found 38 pieces from UAE showrooms and workshops. I prioritized items that are genuinely in stock, solid-timber and full-grain construction over veneer, and sellers who assemble on delivery. Dimensions are on every card so you can check the space before you commit.' : category === 'perfume' ? 'I found 32 fragrances from verified houses. I prioritized authentic stock, longer-lasting concentrations, and bottles available in more than one size. Each one shows its notes and how long it lasts — pick a bottle size to add it.' : category === 'apparel' ? 'I found 46 pieces in stock in your size. I prioritized natural fabrics, verified boutiques, and pieces available right now rather than on pre-order. Choose a colour and size directly on any of them.' : category === 'food' ? 'I found 24 burgers near you. I prioritized kitchens rated 4.5 and above, shorter prep times, and places that let you customise. Tap Choose options on any of them to pick a size, add extras, or leave out anything you don’t want.' : category === 'property' ? 'I found 18 properties that match. I prioritized location, proximity to schools, community quality, and trusted listings.' : category === 'car-rental' ? `I found ${rentalListings.length} rental cars available now, SUVs first. I prioritized verified rental companies, clear daily prices, and cars you can pick up or have delivered today.` : 'I found 27 matching vehicles. I prioritized GCC specs, clean history, lower mileage, and trusted sellers.';
const narrowContentFor = (category: SearchCategory) => category === 'furniture' ? "I can narrow this down — tell me the room, the space you have to fill in centimetres, your budget, or whether you need it this week, and I'll refine these." : category === 'perfume' ? "I can narrow this down — tell me if you prefer woody, floral or fresh, whether it's a gift, and your budget, and I'll refine these." : category === 'apparel' ? "I can narrow this down — tell me your size, a budget, or the occasion you're dressing for, and I'll refine these." : category === 'food' ? "I can narrow this down — tell me a budget, how spicy you like it, whether you want it vegetarian, or how fast you need it, and I'll refine these." : category === 'property' ? "I can narrow this down — tell me your budget, preferred area, number of bedrooms, or which school matters most, and I'll refine these." : category === 'car-rental' ? "I can narrow this down — tell me your dates, how many seats you need, a daily budget, or whether you want it delivered, and I'll refine these." : "I can narrow this down for you — just tell me your budget, preferred color, or any other detail, and I'll refine these matches.";
type SelectedVehicle = {
  id: string;
  title: string;
};
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
  // 'search' = first broad result set (carousel)
  // 'narrow_prompt' = follow-up offer to refine the results
  // 'narrowed' = refined 3-result set
  // 'vehicle_answer' = AI answer about specific selected vehicles
  kind?: 'search' | 'narrow_prompt' | 'narrowed' | 'vehicle_answer' | 'seller_offer' | 'seller_simple' | 'brand_menu' | 'brand_items' | 'live_search';
  vehicles?: SelectedVehicle[];
  /* ---- 'live_search' only: a search that fills in while it runs ---- */
  liveId?: string;
  query?: string;
  startedAt?: number;
  durationMs?: number;
  liveStatus?: LiveSearchStatus;
  revealedAtStop?: number;
  /** Offered after the results settle; omitted for open questions. */
  followUp?: string;
  category?: SearchCategory;
  sellType?: BusinessType;
  /** Set on the brand kinds — the merchant whose menu is shown. */
  brand?: BrandStore;
  /** Set on 'brand_items' — the dishes to show as result cards. */
  brandItems?: BrandMenuItem[];
  /** The department these items came from, so the chip row can mark it. */
  brandCategory?: string;
};
export function Discover({
  searchSpeed = 'normal'
}: {
  /** Prototype-only: how long the simulated search backend takes. */
  searchSpeed?: SearchSpeed;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    activateHub,
    hub
  } = useBusiness();
  const {
    requireAuth
  } = useAuth();
  const isShopMode = !!slug;
  const shopLogo = isShopMode && hub?.shopSlug === slug ? hub.logo : null;
  const shopName = isShopMode && hub?.shopSlug === slug ? hub.name : null;
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [hasNarrowed, setHasNarrowed] = useState(false);
  const [searchCategory, setSearchCategory] = useState<SearchCategory>('cars');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  /** Set while the conversation is scoped to one merchant's menu. */
  const [activeBrand, setActiveBrand] = useState<BrandStore | null>(null);
  const [selectedVehicles, setSelectedVehicles] = useState<SelectedVehicle[]>([]);
  /** What Talab says it's doing during short, non-search replies. */
  const [pendingLabel, setPendingLabel] = useState('Checking what’s available');
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  /** False once the customer scrolls up to read — then new content stops pulling them down. */
  const stickRef = useRef(true);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const names = Array.from(files).map(f => f.name);
    setAttachments(prev => [...prev, ...names]);
    e.target.value = '';
  };
  const removeAttachment = (idx: number) => setAttachments(prev => prev.filter((_, i) => i !== idx));
  const toggleRecording = () => setIsRecording(r => !r);
  // Keep the latest message + input in view as the conversation grows.
  // Scroll the CONTAINER to its full height (more reliable than scrollIntoView
  // when AI replies add tall content/images that grow height after paint).
  useEffect(() => {
    /* The landing page and the thread share one scroll container, so with no
       conversation yet this must NOT follow the bottom — the page opens at the top. */
    if (chatHistory.length === 0 && !isSearching) {
      scrollContainerRef.current?.scrollTo({
        top: 0
      });
      return;
    }
    // Sending a message always brings the thread back to the newest content.
    if (chatHistory[chatHistory.length - 1]?.role === 'user') stickRef.current = true;
    const scrollToBottom = () => {
      const el = scrollContainerRef.current;
      if (el && stickRef.current) el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    };
    const raf = requestAnimationFrame(scrollToBottom);
    const t1 = setTimeout(scrollToBottom, 250);
    const t2 = setTimeout(scrollToBottom, 700);
    /* Result cards load their photography after paint, which grows the thread
       well after the message arrived — so follow the height, not just the
       message count. Stops once the reply has settled. */
    const el = scrollContainerRef.current;
    let observer: ResizeObserver | undefined;
    if (el && 'ResizeObserver' in window) {
      observer = new ResizeObserver(scrollToBottom);
      Array.from(el.children).forEach(c => observer!.observe(c));
    }
    const stop = setTimeout(() => observer?.disconnect(), 2500);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(stop);
      observer?.disconnect();
    };
  }, [chatHistory, isSearching]);
  /* Stop following as soon as the customer scrolls up themselves; resume once
     they return to the bottom. Programmatic scrolls never unstick. */
  const hasThread = chatHistory.length > 0;
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let touchY = 0;
    const nearBottom = () => el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) stickRef.current = false;
    };
    const onTouchStart = (e: TouchEvent) => {
      touchY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      if ((e.touches[0]?.clientY ?? 0) - touchY > 12) stickRef.current = false;
    };
    const onScroll = () => {
      if (nearBottom()) stickRef.current = true;
    };
    el.addEventListener('wheel', onWheel, {
      passive: true
    });
    el.addEventListener('touchstart', onTouchStart, {
      passive: true
    });
    el.addEventListener('touchmove', onTouchMove, {
      passive: true
    });
    el.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('scroll', onScroll);
    };
  }, [hasThread]);
  /** Gently keep a live search's newest content in view — only if wanted. */
  const followLatest = () => {
    const el = scrollContainerRef.current;
    if (el && stickRef.current) el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
  };
  /* The closing message Talab sends already offers to narrow down, so
     completing a search adds nothing further to the thread. */
  const completeLiveSearch = (liveId: string) => {
    setChatHistory(prev => prev.map(m => m.liveId === liveId ? {
      ...m,
      liveStatus: 'done'
    } : m));
  };
  const stopLiveSearch = (liveId: string, revealed: number) => {
    setChatHistory(prev => prev.map(m => m.liveId === liveId ? {
      ...m,
      liveStatus: 'stopped',
      revealedAtStop: revealed
    } : m));
  };
  const liveSearchRunning = chatHistory.some(m => m.kind === 'live_search' && m.liveStatus === 'running');
  const scrollToListing = (id: string) => {
    const el = document.getElementById(`listing-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
      el.classList.add('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      }, 1600);
    }
  };
  const resetConversation = () => {
    setChatHistory([]);
    setHasResults(false);
    setHasNarrowed(false);
    setSelectedVehicles([]);
    setSearchCategory('cars');
    setQuery('');
    setIsSearching(false);
    setActiveBrand(null);
  };

  /**
   * A follow-up inside a brand conversation. Matches the merchant's own
   * departments first, then dish names and ingredients, then a budget — and
   * always answers with the same result cards the marketplace uses.
   */
  const answerWithinBrand = (brand: BrandStore, text: string) => {
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setPendingLabel(`Checking the ${brand.name} menu`);
    setIsSearching(true);
    const t = text.toLowerCase().trim();
    const budget = t.match(/(\d{2,4})/);
    const wantsBudget = /under|below|less than|cheap/.test(t) && budget;
    const cat = brand.categories.find(c => t.includes(c.toLowerCase()) || c.toLowerCase().includes(t));
    let items = cat ? brand.items.filter(i => i.category === cat) : brand.items.filter(i => `${i.title} ${i.category} ${i.dish.included.map(x => x.label).join(' ')}`.toLowerCase().includes(t));
    if (wantsBudget) {
      const cap = Number(budget![1]);
      items = brand.items.filter(i => i.dish.sizes.some(s => s.price <= cap));
    }
    // Nothing matched — offer the whole menu rather than a dead end.
    const fallback = items.length === 0;
    if (fallback) items = brand.items.filter(i => i.popular);
    setTimeout(() => {
      setIsSearching(false);
      setHasResults(true);
      setChatHistory(prev => [...prev, {
        role: 'ai',
        kind: 'brand_items',
        brand,
        brandItems: items,
        brandCategory: fallback || wantsBudget ? undefined : cat,
        content: fallback ? `I couldn't find that on the ${brand.name} menu. Here's what people order most — or pick a section above.` : wantsBudget ? `${items.length} items at ${brand.name} come in under AED ${budget![1]}. Prices shown are the smallest size.` : cat ? `${cat} at ${brand.name} — ${items.length} ${items.length === 1 ? 'item' : 'items'}. Tap any one to choose a size, take something out, or add extras.` : `${items.length} ${items.length === 1 ? 'item' : 'items'} on the ${brand.name} menu match “${text.trim()}”.`
      }]);
    }, 1200);
  };
  const pickBrandCategory = (brand: BrandStore, category: string) => {
    setActiveBrand(brand);
    answerWithinBrand(brand, `Show me ${category}`);
  };
  // Clicking "Discover" in the nav navigates to "/" with a fresh `reset`
  // timestamp — start a brand-new chat even if already on this route.
  useEffect(() => {
    if (location.state && (location.state as {
      reset?: number;
    }).reset) {
      resetConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  // Business seller accepted — require sign-in, then create the workspace
  // and take the user straight into the Business Hub.
  const createBusinessHub = (type: BusinessType) => {
    requireAuth(() => {
      activateHub(type);
      navigate('/business');
    });
  };
  // Business seller declined — fall back to a single listing in chat
  const continueWithOneListing = () => {
    setChatHistory(prev => [...prev, {
      role: 'ai',
      kind: 'seller_simple',
      content: "No problem — let's create a single listing right here. Upload a few photos and tell me the key details, and I'll generate a title, description, and a suggested price for you to review before publishing."
    }]);
  };
  const toggleVehicle = (id: string, title: string) => {
    setSelectedVehicles(prev => prev.some(v => v.id === id) ? prev.filter(v => v.id !== id) : [...prev, {
      id,
      title
    }]);
  };
  // Attach selection state + handler to a listing before rendering
  const withSelection = (listing: ListingCardProps): ListingCardProps => ({
    ...listing,
    selected: selectedVehicles.some(v => v.id === listing.id),
    onToggleSelect: () => toggleVehicle(listing.id, listing.title)
  });
  const runQuery = (text: string) => {
    if (!text.trim() && selectedVehicles.length === 0) return;
    // If vehicles are selected, this is a specific question about them
    if (selectedVehicles.length > 0) {
      const vehicles = selectedVehicles;
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text || 'Tell me more about these',
        vehicles
      }]);
      setQuery('');
      setSelectedVehicles([]);
      setPendingLabel(vehicles.length > 1 ? 'Comparing those for you' : 'Checking the details');
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        const names = vehicles.map(v => v.title).join(' and ');
        const answer = vehicles.length > 1 ? `Comparing ${names}: both are GCC-spec and well-maintained. The newer one holds resale value better and has lower mileage, while the other is more affordable upfront. For long-term ownership I'd lean toward the lower-mileage option; for budget, the cheaper one is the smarter buy.` : `About the ${names}: it's a GCC-spec unit with a clean history and fair pricing versus the market. Service records check out and the mileage is reasonable for the year. It's a solid, low-risk choice — happy to dig into anything specific like financing, inspection, or negotiation room.`;
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'vehicle_answer',
          content: answer,
          vehicles
        }]);
      }, 1400);
      return;
    }
    // Detect seller intent — route to a one-time listing or a Business Hub offer
    const sellIntent = detectSellIntent(text);
    if (sellIntent) {
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text
      }]);
      setQuery('');
      setPendingLabel('Getting that ready for you');
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        if (sellIntent.tier === 'individual') {
          // One-time seller — stay in chat, create a simple listing
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_simple',
            content: "Great — upload a few photos and I'll help create the listing. I'll just need a few details: make/model, year, mileage, condition, accident history, GCC specs, and your asking price. Then I'll generate a title, description, and a suggested price for you to review."
          }]);
        } else {
          // Business / enterprise — offer to create a Business Hub
          const intro = sellIntent.tier === 'enterprise' ? `It sounds like you operate a business with multiple listings and customer inquiries. I recommend creating a Business Hub to manage your ${BUSINESS_LABEL_INLINE[sellIntent.type]}, receive leads, track inquiries, manage team access, and get your own shareable shop link to send to customers.` : `It sounds like you're managing multiple ${BUSINESS_LABEL_INLINE[sellIntent.type]}. I can create a Business Hub for you to manage inventory, leads, messages, and analytics in one place. Would you like me to set it up?`;
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_offer',
            content: intro,
            sellType: sellIntent.type
          }]);
        }
      }, 1400);
      return;
    }
    /* A named brand is a different intent: the shopper already knows the
       merchant, so the answer is the shape of that merchant's menu first —
       departments as cards — and the dishes themselves one step later. */
    const brand = findBrand(text);
    if (brand) {
      setActiveBrand(brand);
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text
      }]);
      setQuery('');
      setPendingLabel(`Opening the ${brand.name} menu`);
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        setHasResults(true);
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'brand_menu',
          brand,
          content: `${brand.name} at ${brand.branch} is open and delivering to you in ${brand.deliveryTime}. Their menu runs to ${brand.items.length} items across ${brand.categories.length} sections — pick one to see what's in it.`
        }]);
      }, 1400);
      return;
    }
    /* Inside a brand conversation every follow-up stays inside that menu, so
       "chicken" means their chicken, not the whole marketplace's. */
    if (activeBrand) {
      answerWithinBrand(activeBrand, text);
      return;
    }
    const firstSearch = !hasResults;
    const category = firstSearch ? detectCategory(text) : searchCategory;
    /* A fresh search fills in while it runs: Talab acknowledges, narrates
       what it's doing, shows card placeholders, then reveals products one by
       one — the customer never waits on a blank bubble. */
    if (firstSearch) {
      setSearchCategory(category);
      setHasResults(true);
      const narration = narrateSearch(text, category);
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text
      }, {
        role: 'ai',
        kind: 'live_search',
        liveId: `live-${Date.now()}`,
        query: text,
        category,
        content: narration.openQuestion ?? searchContentFor(category),
        startedAt: Date.now(),
        durationMs: SEARCH_SPEED_MS[searchSpeed],
        liveStatus: 'running'
      }]);
      setQuery('');
      return;
    }
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setPendingLabel('Narrowing down your matches');
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      setHasNarrowed(true);
      setChatHistory(prev => [...prev, {
        role: 'ai',
        kind: 'narrowed',
        category,
        content: "Perfect — based on what you told me, I've narrowed it down to just 3 strong matches that fit your preferences best."
      }]);
    }, 1500);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    // Fold any attachments into the query context, then clear them
    const attachmentNote = attachments.length > 0 ? ` [Attached: ${attachments.join(', ')}]` : '';
    runQuery(`${query}${attachmentNote}`.trim());
    setAttachments([]);
    setIsRecording(false);
  };
  const handlePromptClick = (prompt: string) => {
    runQuery(prompt);
  };
  return (
    /* Dynamic viewport units: on mobile browsers 100vh is taller than what is
       actually visible, which pushed the composer below the fold and made the
       whole page scroll instead of just the thread. */
    <div className="app-fill-screen flex-1 min-h-0 flex flex-col h-[calc(100dvh-57px)] max-h-[calc(100dvh-57px)] lg:h-[100dvh] lg:max-h-[100dvh] bg-white relative overflow-hidden" data-id="element-5323" data-mp="10jbpe202rqzk">
      {/* Hidden file pickers for attachments */}
      <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleFilesSelected} className="hidden" data-id="element-5324" data-mp="qu4trc4zh2ub" />
      <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.csv,.xlsx,.txt" multiple onChange={handleFilesSelected} className="hidden" data-id="element-5325" data-mp="lwv53ypf4v7j" />
      {/* Background — refined architectural canvas: hairline grid, micro texture,
          concentric signal arcs. Deliberately no illustrated icons. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true" data-id="element-5326" data-mp="15jsuab5cbxgz">
        {/* Micro dot texture — gives the white surface depth up close */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(67, 56, 202, 0.055) 1px, transparent 0)',
          backgroundSize: '26px 26px'
        }} data-id="element-5327" data-mp="1ekimz5zb17qh" />

        {/* Category marks — hairline icons set in frosted tiles, composed along the
            margins so the centre column always stays clear */}
        {[{
          Icon: CarIcon,
          cls: 'top-[15%] left-[4%] w-[78px] h-[78px]',
          r: -8,
          d: 0
        }, {
          Icon: HomeIcon,
          cls: 'top-[44%] left-[8%] w-[58px] h-[58px]',
          r: 6,
          d: 1.4
        }, {
          Icon: BriefcaseIcon,
          cls: 'bottom-[13%] left-[5%] w-[68px] h-[68px]',
          r: -5,
          d: 2.2
        }, {
          Icon: GraduationCapIcon,
          cls: 'bottom-[34%] left-[14%] w-[46px] h-[46px]',
          r: 9,
          d: 3
        }, {
          Icon: ShoppingBagIcon,
          cls: 'top-[13%] right-[5%] w-[64px] h-[64px]',
          r: 7,
          d: 0.8
        }, {
          Icon: PlaneIcon,
          cls: 'top-[43%] right-[3.5%] w-[78px] h-[78px]',
          r: -6,
          d: 1.8
        }, {
          Icon: WrenchIcon,
          cls: 'bottom-[15%] right-[7%] w-[58px] h-[58px]',
          r: 5,
          d: 2.6
        }, {
          Icon: SmartphoneIcon,
          cls: 'bottom-[36%] right-[15%] w-[46px] h-[46px]',
          r: -9,
          d: 3.4
        }].map(({
          Icon,
          cls,
          r,
          d
        }, i) => <motion.div key={i} className={`hidden lg:flex absolute ${cls} items-center justify-center rounded-2xl border border-primary-indigo/[0.09] bg-white/55 backdrop-blur-[2px] shadow-[0_12px_32px_rgba(11,18,32,0.045)]`} style={{
          rotate: r
        }} animate={{
          y: [0, -8, 0]
        }} transition={{
          duration: 14 + i % 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: d
        }} data-id="element-5328" data-mp="pvkof7fqyu9" data-mpi={i}>
            <Icon className="w-1/2 h-1/2 text-primary-indigo/[0.22]" strokeWidth={1} data-id="element-5329" data-mp="3amug891iu6tz" />
          </motion.div>)}

        {/* Ambient light from the top, then a white vignette so edges stay clean */}
        <div className="absolute inset-x-0 top-0 h-[60%]" style={{
          background: 'radial-gradient(65% 100% at 50% 0%, rgba(67, 56, 202, 0.055), transparent 72%)'
        }} data-id="element-5330" data-mp="76eza9j2n29h" />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(125% 95% at 50% 42%, transparent 38%, rgba(255, 255, 255, 0.92) 100%)'
        }} data-id="element-5331" data-mp="26iu46yn14sc" />

      </div>

      {/* Chat toolbar — always visible above the thread (never scrolls away) */}
      {(hasResults || isSearching || chatHistory.length > 0) && <div className="flex-shrink-0 px-4 lg:px-12 pt-4 pb-3 relative z-30 bg-white/60 backdrop-blur-md after:content-[''] after:absolute after:inset-x-0 after:top-full after:h-6 after:bg-gradient-to-b after:from-white/60 after:to-transparent after:pointer-events-none" data-id="element-5332" data-mp="2ffbpbq2u46jl">
          <div className="max-w-6xl mx-auto w-full" data-id="element-5333" data-mp="2kcv36ovmehb0">
            <button onClick={resetConversation} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-5334" data-mp="1wmhjpmr3pqs1">
              <PlusIcon className="w-4 h-4" data-id="element-5335" data-mp="21laomqsbo9gl" />
              New request
            </button>
          </div>
        </div>}

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden px-4 lg:px-12 pb-32 lg:pb-28 pt-2 lg:pt-0 relative z-10" data-id="element-5336" data-mp="1duyi9kcq6ks4">
        <div className="max-w-6xl mx-auto w-full h-full flex flex-col min-w-0" data-id="element-5337" data-mp="nedbi4mxxlcv">
          {!hasResults && !isSearching && chatHistory.length === 0 ?
          // Initial State (Hero)
          <div className="flex-1 flex flex-col items-center justify-center py-6" data-id="element-5338" data-mp="xc9jinkl006r">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="w-full max-w-6xl flex flex-col items-center relative z-10" data-id="element-5339" data-mp="11dms35f26hhd">
                {/* Top Icon / Logo */}
                <div className="relative mb-4" data-id="element-5340" data-mp="1g3ats7r5ji5m">
                  <div className="absolute -inset-3 border border-primary-indigo/[0.12] rounded-full" data-id="element-5341" data-mp="1l23wd54whor0" />
                  <div className="absolute -inset-7 border border-primary-indigo/[0.07] rounded-full" data-id="element-5342" data-mp="1p3pf37448x94" />
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white rounded-full flex items-center justify-center shadow-soft border border-slate-100 relative z-10 overflow-hidden" data-id="element-5343" data-mp="1u1img3h5nxaz">
                    {isShopMode && shopLogo ? <img src={shopLogo} alt={shopName || 'Shop'} className="w-full h-full object-cover" data-id="element-5344" data-mp="39jooc3tx4ole" /> : <SparklesIcon className="w-8 h-8 lg:w-10 lg:h-10 text-primary-indigo" data-id="element-5345" data-mp="30mwh41a08hyy" />}
                  </div>
                </div>

                {/* Main Heading */}
                <div className="text-center space-y-2.5 mb-6" data-id="element-5346" data-mp="3tbl8h8l9d8xi">
                  {isShopMode ? <>
                      <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-5347" data-mp="3jjny2422qkzu">
                        Welcome to{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-5348" data-mp="kvicw17f7apj">
                          {shopName || 'Our Shop'}
                        </span>
                      </h1>
                      <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-5349" data-mp="3v5p1banaz41">
                        Ask our AI assistant anything about our inventory,
                        pricing, or services.
                      </p>
                    </> : <>
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-100 shadow-sm text-[11px] font-bold uppercase tracking-wider text-primary-indigo" data-id="element-5350" data-mp="qua7treccfwf">
                        <SparklesIcon className="w-3.5 h-3.5" data-id="element-5351" data-mp="10jtr2y5yubrt" />
                        Live demo · See what your customers would get
                      </span>
                      <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-5352" data-mp="1aj2qi6jloilk">
                        Your customers just{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-5353" data-mp="15ieqivde8rf7">
                          ask
                        </span>
                      </h1>
                      <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-5354" data-mp="1f7u4hsndnd31">
                        No filters, no menus, no search. Try it below on our
                        demo shops — then give your own business the same thing.
                      </p>
                    </>}
                </div>

                {/* Category Ribbon — full width, evenly distributed, no scroll */}
                <div className="w-full max-w-5xl mb-6" data-id="element-5355" data-mp="wif3tzblspz6">
                  <CategoryRibbon items={categoryRibbonItems} onSelect={item => {
                  setQuery(item.prompt + ' ');
                  textareaRef.current?.focus();
                }} data-id="element-5356" data-mp="11gglcftdsh8j" />
                </div>

                {/* AI Input Area */}
                {/* Focus: no blurred halo. A crisp hairline in the same
                    drifting indigo→purple as the header controls draws the
                    edge, and the shadow takes on a faint indigo cast. */}
                <div className="w-full max-w-3xl bg-white rounded-3xl border border-slate-100 focus-within:border-transparent p-2 mb-8 relative isolate group z-10 shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.11)] focus-within:shadow-[0_14px_40px_-12px_rgba(79,70,229,0.28)] transition-[box-shadow,border-color] duration-300 ease-out" data-id="element-5357" data-mp="3uu0ijg0bdvp7">
                  <ComposerRing data-id="element-5358" data-mp="he2mpegt9vji" />
                  <div className="relative bg-white rounded-[22px] p-5 flex flex-col gap-3" data-id="element-5359" data-mp="hd17cxsrimvz">
                    <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }} placeholder={isShopMode ? `Ask ${shopName || 'us'} anything...` : 'Ask Talab anything...'} className="w-full resize-none outline-none text-base lg:text-lg text-slate-800 placeholder:text-slate-300 bg-transparent min-h-[44px]" rows={1} data-id="element-5360" data-mp="ce7vtax1d5yg" />

                    {/* Attachment chips */}
                    {attachments.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-5361" data-mp="25a0ms3by7jxb">
                        {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 text-slate-600 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full" data-id="element-5362" data-mp="ay2eq4pdku30" data-mpi={i}>
                            <PaperclipIcon className="w-3.5 h-3.5" data-id="element-5363" data-mp="26cfz8a6w02ko" />
                            <span className="max-w-[140px] truncate" data-id="element-5364" data-mp="21e4pdzglds2g">
                              {name}
                            </span>
                            <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-200 rounded-full" aria-label={`Remove ${name}`} data-id="element-5365" data-mp="2fcmikcp9nwrr">
                              <XIcon className="w-3.5 h-3.5" data-id="element-5366" data-mp="1iq9hgw7nv8h9" />
                            </button>
                          </span>)}
                      </div>}

                    <div className="flex items-center justify-between mt-2" data-id="element-5367" data-mp="1ro393tuhddy3">
                      {/* Verb Row */}
                      <div className="flex items-center gap-3 text-[11px] font-semibold text-slate-400 uppercase tracking-widest hidden sm:flex" data-id="element-5368" data-mp="13xfnk5rn6pt2">
                        <span data-id="element-5369" data-mp="pu0akdyoag8d">Find</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-5370" data-mp="ps6yuk97d5ti" />
                        <span data-id="element-5371" data-mp="ulrxmeime3tr">Buy</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-5372" data-mp="fw3y9iu01f8o" />
                        <span data-id="element-5373" data-mp="fxx3ctoyrrek">Sell</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-5374" data-mp="kunldmkbwx2m" />
                        <span data-id="element-5375" data-mp="18kroztxiowtm">Hire</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-5376" data-mp="18lt01yej9jbc" />
                        <span data-id="element-5377" data-mp="1dke1u29fob1w">Rent</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-5378" data-mp="1djceayb8dw3a" />
                        <span data-id="element-5379" data-mp="ylk0qbp8vhrq">Compare</span>
                      </div>

                      <div className="flex items-center gap-1 ml-auto" data-id="element-5380" data-mp="13lessikraded">
                        <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2.5 rounded-full transition-colors ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-5381" data-mp="13nrf17xjr9x8">
                          <MicIcon className="w-5 h-5" data-id="element-5382" data-mp="1pw6o0q8sz0up" />
                        </button>
                        <button type="button" onClick={() => imageInputRef.current?.click()} aria-label="Attach image" className="p-2.5 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors" data-id="element-5383" data-mp="1l1izwshpo8gn">
                          <ImageIcon className="w-5 h-5" data-id="element-5384" data-mp="123pvtmvcpsho" />
                        </button>
                        <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach file" className="p-2.5 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors" data-id="element-5385" data-mp="x5y16l3bns9s">
                          <PaperclipIcon className="w-5 h-5" data-id="element-5386" data-mp="2z0vwj0gh8tsq" />
                        </button>
                        <SendButton ready={!!query.trim() || attachments.length > 0} onClick={() => handleSubmit()} data-id="element-5387" data-mp="3408eb6shraog" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Example Prompts */}
                <div className="w-full max-w-5xl mb-8 z-10 relative" data-id="element-5388" data-mp="2l2xixx9zd1ea">
                  <div className="flex items-center justify-center gap-4 mb-5" data-id="element-5389" data-mp="2l59yy4w6h1op">
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-5390" data-mp="2q0pb8sejepj0" />
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-5391" data-mp="2v2ema81rf373">
                      Ask one of our demo shops
                    </p>
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-5392" data-mp="aaw1yj26j37z" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-5393" data-mp="vex19mr628wy">
                    {suggestedPrompts.map((prompt, index) => {
                    const Icon = prompt.icon;
                    return <motion.button key={index} initial={{
                      opacity: 0,
                      y: 10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.2 + index * 0.05
                    }} onClick={() => handlePromptClick(prompt.text)} className="flex items-center justify-between px-4 py-3 bg-white border border-slate-100 rounded-2xl hover:border-slate-200 hover:bg-slate-50/50 transition-all text-left group shadow-sm" data-id="element-5394" data-mp="155rpuxrvxklk" data-mpi={index}>
                          <div className="flex items-center gap-3" data-id="element-5395" data-mp="20f4vgdb92grl">
                            <div className="p-2 bg-slate-50 rounded-lg group-hover:bg-white transition-colors" data-id="element-5396" data-mp="25cwsflqr4t2n">
                              <Icon className="w-4 h-4 text-primary-indigo/60" data-id="element-5397" data-mp="1qp222c07v0sq" />
                            </div>
                            <span className="text-[13px] font-medium text-slate-600 leading-snug pr-2" data-id="element-5398" data-mp="1vmbqbobvd6ez">
                              {prompt.text}
                            </span>
                          </div>
                          <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-primary-indigo/50 transition-colors flex-shrink-0" data-id="element-5399" data-mp="1mo9s1gjisrve" />
                        </motion.button>;
                  })}
                  </div>
                </div>

                {!isShopMode && <BusinessPitch data-id="element-5400" data-mp="324qo915nbvsh" />}
              </motion.div>
            </div> :
          // Chat History State
          <div className="space-y-6 pb-36 pt-2" data-id="element-5401" data-mp="370fqjc91vqsh">
              {chatHistory.map((msg, idx) => <div key={idx} data-id="element-5402" data-mp="ef7nu0mxd7po" data-mpi={idx}>
                  {msg.role === 'user' ? <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex justify-end mb-6" data-id="element-5403" data-mp="j9lks7h8jmwg">
                      <div className="bg-slate-100 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%] shadow-sm" data-id="element-5404" data-mp="127epnfbf66wh">
                        {msg.vehicles && msg.vehicles.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-5405" data-mp="t5yf97klu4s9">
                            {msg.vehicles.map(v => <span key={v.id} className="inline-flex items-center gap-1.5 bg-white border border-primary-indigo/20 text-primary-indigo text-xs font-medium px-3 py-1.5 rounded-full" data-id="element-5406" data-mp="38wb6uyqiyhhu">
                                <SparklesIcon className="w-3.5 h-3.5" data-id="element-5407" data-mp="31emr8swidxpl" />
                                {v.title}
                              </span>)}
                          </div>}
                        <p className="text-slate-800 text-[15px]" data-id="element-5408" data-mp="3afb5nopte030">
                          {msg.content}
                        </p>
                      </div>
                    </motion.div> : msg.kind === 'live_search' && msg.liveId ? <LiveSearchResponse narration={narrateSearch(msg.query ?? '', msg.category ?? 'cars')} listings={listingsFor(msg.category ?? 'cars').map(withSelection)} startedAt={msg.startedAt ?? 0} durationMs={msg.durationMs ?? 0} status={msg.liveStatus ?? 'done'} revealedAtStop={msg.revealedAtStop} onComplete={() => completeLiveSearch(msg.liveId!)} onStop={n => stopLiveSearch(msg.liveId!, n)} onProgress={followLatest} data-id="element-5409" data-mp="2wf0dmicrqfg9" /> : <div className="mb-6" data-id="element-5410" data-mp="3g3jkb1gerxqm">
                      <AIResponse message={msg.content} insights={msg.kind === 'search' ? msg.category === 'property' ? propertyInsights : mockInsights : undefined} onInsightClick={scrollToListing} data-id="element-5411" data-mp="3g4kyvfwmv2d3" />

                      {msg.kind === 'brand_menu' && msg.brand && <div className="mt-4 ml-0 md:ml-14" data-id="element-5412" data-mp="2tu31tj3sbsdj">
                          <BrandCategoryCards store={msg.brand} onPick={c => pickBrandCategory(msg.brand!, c)} data-id="element-5413" data-mp="2ttjgrtiu8nva" />
                        </div>}

                      {msg.kind === 'brand_items' && msg.brand && msg.brandItems && <div className="mt-4 ml-0 md:ml-14 space-y-4" data-id="element-5414" data-mp="2b5ekf74ig56i">
                            {/* The merchant's sections stay one tap away */}
                            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-id="element-5415" data-mp="222vgejoznmio">
                              {msg.brand.categories.map(c => {
                      const active = msg.brandCategory === c;
                      return <button key={c} onClick={() => pickBrandCategory(msg.brand!, c)} aria-current={active ? 'true' : undefined} className={`flex-shrink-0 px-3 py-1.5 rounded-full border text-[13px] font-semibold transition-colors duration-150 ease-out ${active ? 'bg-dark-navy text-white border-dark-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-primary-indigo hover:text-primary-indigo'}`} data-id="element-5416" data-mp="3ri3duaij08yy">
                                    {c}
                                  </button>;
                    })}
                            </div>
                            {/* Denser than a mixed-merchant result row: these
                                are all one kitchen, so more fit per screen. */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-5417" data-mp="1rgn7snzvpy0s">
                              {msg.brandItems.map(item => <ListingCard key={item.id} {...item} compact data-id="element-5418" data-mp="17vn0egyw7ket" />)}
                            </div>
                          </div>}

                      {msg.kind === 'seller_offer' && <div className="flex flex-col sm:flex-row gap-3 mt-4 ml-0 md:ml-14" data-id="element-5419" data-mp="1ctol16byvrse">
                          <button onClick={() => createBusinessHub(msg.sellType ?? 'generic')} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-primary-indigo to-ai-purple text-white rounded-xl font-semibold shadow-sm hover:brightness-110 transition-all" data-id="element-5420" data-mp="1qth88roabpvm">
                            <BriefcaseIcon className="w-5 h-5" data-id="element-5421" data-mp="29l0a2v6koxw2" />
                            Yes, create Business Hub
                          </button>
                          <button onClick={continueWithOneListing} className="inline-flex items-center justify-center px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-5422" data-mp="2cmbv2qix9kvx">
                            Continue with one listing
                          </button>
                        </div>}

                      {msg.kind === 'narrowed' ?
                // Narrowed: 3 strong matches
                <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.4
                }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5 ml-0 md:ml-12" data-id="element-5423" data-mp="35a7nhj1gkqmw">
                          {(msg.category === 'electronics' ? electronicsNarrowed : msg.category === 'furniture' ? furnitureNarrowed : msg.category === 'perfume' ? perfumeNarrowed : msg.category === 'apparel' ? apparelNarrowed : msg.category === 'food' ? foodNarrowed : msg.category === 'property' ? propertyNarrowed.map(l => ({
                    ...l,
                    sector: 'property' as const
                  })) : msg.category === 'car-rental' ? rentalNarrowed : narrowedListings).map((listing, lIdx) => <motion.div key={listing.id} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.5 + lIdx * 0.1
                  }} data-id="element-5424" data-mp="vmqcgqbzz1hb" data-mpi={lIdx}>
                              <ListingCard {...withSelection(listing)} data-id="element-5425" data-mp="14mdg7is78jdj" />
                            </motion.div>)}
                        </motion.div> : msg.kind === 'search' ?
                // Broad search: swipeable carousel
                <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.4
                }} className="mt-6 ml-0 md:ml-14" data-id="element-5426" data-mp="3htd0vbinwgri">
                          <div className="flex items-center justify-between mb-3 pr-4" data-id="element-5427" data-mp="2yvk12i3c4myc">
                            <span className="text-sm font-medium text-slate-500" data-id="element-5428" data-mp="2tw795ixlwpow">
                              {countLabelFor(msg.category ?? 'cars')}
                            </span>
                            <span className="text-xs text-slate-400 hidden md:inline" data-id="element-5429" data-mp="33t29m7dfx05t">
                              {hintFor(msg.category ?? 'cars')}
                            </span>
                          </div>
                          <ListingCarousel listings={listingsFor(msg.category ?? 'cars').map(withSelection)} data-id="element-5430" data-mp="j2b9yy5x5di7" />
                        </motion.div> : null}
                    </div>}
                </div>)}

              {isSearching && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2
            }} className="flex gap-4 items-center" aria-live="polite" data-id="element-5431" data-mp="itygoq2unilt">
                  <AssistantMark data-id="element-5432" data-mp="11riv137zmys6" />
                  <p className="flex items-center text-[13.5px] text-slate-500" data-id="element-5433" data-mp="11qhkr0ysqgbi">
                    {pendingLabel}
                    <TypingDots data-id="element-5434" data-mp="1z8hm2ia5s467" />
                  </p>
                </motion.div>}
              {/* The moment the pitch actually lands: right after they have
                  seen Talab answer a real customer question. */}
              {!isShopMode && !isSearching && !liveSearchRunning && chatHistory.length > 0 && <DemoFooterCta data-id="element-5435" data-mp="2bohdv5hxzaju" />}
              {/* Scroll anchor — keeps newest message + input in view */}
              <div ref={bottomRef} className="h-px" data-id="element-5436" data-mp="2gpwvsootp0y0" />
            </div>}
        </div>
      </div>

      {/* Floating Input (always visible while chatting) */}
      {(hasResults || isSearching || chatHistory.length > 0) && <>
          {/* Soft fade so content dissolves behind the floating pill */}
          <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none z-20" data-id="element-5437" data-mp="2uicu5c6s4asl" />
          <div className="absolute bottom-5 left-0 right-0 px-4 lg:px-8 z-30 pointer-events-none flex justify-center" data-id="element-5438" data-mp="34f91vfoussw3">
            <div className="w-full max-w-3xl pointer-events-auto" data-id="element-5439" data-mp="39dso7h7d06oz">
              {/* Selected vehicle tags */}
              {selectedVehicles.length > 0 && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-5440" data-mp="2eqke5t6va2hh">
                  <span className="text-xs font-medium text-slate-500 mr-1" data-id="element-5441" data-mp="32e219bf6sce4">
                    Asking about:
                  </span>
                  {selectedVehicles.map(v => <span key={v.id} className="inline-flex items-center gap-1.5 bg-white border border-primary-indigo/30 text-primary-indigo text-xs font-medium pl-3 pr-2 py-1.5 rounded-full shadow-sm" data-id="element-5442" data-mp="3cchv3m5llzrb">
                      <SparklesIcon className="w-3.5 h-3.5" data-id="element-5443" data-mp="1d1890ma1mij4" />
                      {v.title}
                      <button onClick={() => toggleVehicle(v.id, v.title)} aria-label={`Remove ${v.title}`} className="ml-0.5 p-0.5 hover:bg-primary-indigo/10 rounded-full transition-colors" data-id="element-5444" data-mp="3onijqtrt8hr7">
                        <XIcon className="w-3.5 h-3.5" data-id="element-5445" data-mp="3jx26b9nzow4u" />
                      </button>
                    </span>)}
                </motion.div>}
              {/* Attachment chips */}
              {attachments.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-5446" data-mp="3a0h0wn825j0h">
                  {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full shadow-sm" data-id="element-5447" data-mp="30yg4ttok930e" data-mpi={i}>
                      <PaperclipIcon className="w-3.5 h-3.5" data-id="element-5448" data-mp="z6kvvltovclv" />
                      <span className="max-w-[140px] truncate" data-id="element-5449" data-mp="2var8vzvfxxrv">{name}</span>
                      <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-100 rounded-full" aria-label={`Remove ${name}`} data-id="element-5450" data-mp="2qb4xlvz6arlp">
                        <XIcon className="w-3.5 h-3.5" data-id="element-5451" data-mp="2h9ew1xbkuca7" />
                      </button>
                    </span>)}
                </div>}
              <div className="w-full bg-white rounded-3xl border border-slate-100 focus-within:border-transparent p-2 relative group shadow-[0_8px_30px_rgb(0,0,0,0.12)] focus-within:shadow-[0_14px_40px_-12px_rgba(79,70,229,0.3)] transition-[box-shadow,border-color] duration-300 ease-out" data-id="element-5452" data-mp="27kwwtkiy4d8o">
                <ComposerRing data-id="element-5453" data-mp="1tkvvk3yrr3so" />
                <div className="relative bg-white rounded-3xl flex items-end gap-2 px-4 py-2" data-id="element-5454" data-mp="1tju9z5k4mywp">
                  <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach file" className="p-2 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors flex-shrink-0 mb-0.5" data-id="element-5455" data-mp="1olj0ivxquqf0">
                    <PaperclipIcon className="w-5 h-5" data-id="element-5456" data-mp="e2pjjasakw4j" />
                  </button>
                  <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }} rows={1} placeholder={selectedVehicles.length > 0 ? `Ask about ${selectedVehicles.length > 1 ? 'these vehicles' : 'this vehicle'}...` : 'Ask Talab anything...'} className="flex-1 bg-transparent outline-none text-slate-800 placeholder:text-slate-400 resize-none py-2 max-h-32 leading-relaxed" data-id="element-5457" data-mp="i2zqoqrtlskz" />
                  <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2 rounded-full transition-colors flex-shrink-0 mb-0.5 ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-5458" data-mp="4y2wb7w38j9z">
                    <MicIcon className="w-5 h-5" data-id="element-5459" data-mp="3i8a011uxgvkm" />
                  </button>
                  <SendButton size="sm" ready={(!!query.trim() || selectedVehicles.length > 0 || attachments.length > 0) && !isSearching} onClick={() => handleSubmit()} data-id="element-5460" data-mp="1cdwdwt9ble" />
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>
  );
}

/** The same offer, stated in one line once they've seen an answer. */
function DemoFooterCta() {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    requireAuth
  } = useAuth();
  return <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-primary-indigo/20 bg-primary-indigo/[0.03] px-5 py-4" data-id="element-5461" data-mp="19z3uwsrnr4jl">
      <p className="min-w-0 flex-1" data-id="element-5462" data-mp="1xpzi1cl65bki">
        <span className="block text-[14px] font-bold text-dark-navy" data-id="element-5463" data-mp="1xr0u9kdx843t">
          That was a demo shop answering.
        </span>
        <span className="block text-[13px] text-slate-500 mt-0.5" data-id="element-5464" data-mp="1nzem5wwvql5a">
          Your business can answer customers the same way, from your own prices
          and stock.
        </span>
      </p>
      <CreateShopButton onClick={() => isAuthenticated ? navigate('/business') : requireAuth(() => navigate('/business'))} data-id="element-5465" data-mp="2gnslqh3m9piy" />
    </div>;
}

/**
 * Talab has no public marketplace yet — every shop above is a demo. So the
 * homepage's job is to let a business owner feel the customer experience
 * first, then explain in plain words that they can have it for themselves.
 * Deliberately avoids the word "storefront": most owners don't use it.
 */
function BusinessPitch() {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    requireAuth
  } = useAuth();
  const start = () => isAuthenticated ? navigate('/business') : requireAuth(() => navigate('/business'));
  const points = [{
    icon: ShoppingBagIcon,
    title: 'Add what you sell',
    detail: 'Upload a spreadsheet, connect your website, or add products one by one.'
  }, {
    icon: SparklesIcon,
    title: 'Talab learns your business',
    detail: 'Your prices, stock, delivery and rules — so it answers like your best salesperson.'
  }, {
    icon: UserIcon,
    title: 'Customers chat, you get orders',
    detail: 'On your own Talab page, or added to the website you already have.'
  }];
  return <motion.section initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.28,
    delay: 0.35,
    ease: [0.23, 1, 0.32, 1]
  }} className="w-full max-w-5xl mb-10 z-10 relative" data-id="element-5466" data-mp="35vy7zvj6n7u5">
      <div className="rounded-3xl bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.05)] p-7 lg:p-9" data-id="element-5467" data-mp="3jonsfvj2c4p5">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4" data-id="element-5468" data-mp="3olny45c035fq">
          <div className="min-w-0" data-id="element-5469" data-mp="3tn3ma69h5s2c">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-5470" data-mp="3tjz7yg2apfxy">
              Everything above is a demo shop
            </p>
            <h2 className="font-display font-bold text-xl lg:text-2xl text-dark-navy tracking-tight mt-2" data-id="element-5471" data-mp="1z8sy800pf1xm">
              Give your own business a chat like this
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed mt-2 max-w-xl" data-id="element-5472" data-mp="3gxtrym690pg3">
              Your customers describe what they want, and Talab answers from
              your real prices and stock — day or night.
            </p>
          </div>
          <CreateShopButton size="lg" onClick={start} data-id="element-5473" data-mp="3qnvkbq5mwu0t" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8 pt-7 border-t border-slate-100" data-id="element-5474" data-mp="3vmq16mk1k8ia">
          {points.map(p => <div key={p.title} data-id="element-5475" data-mp="2y097rsefif1t">
              <span className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center" data-id="element-5476" data-mp="32xsu7fla5y4u">
                <p.icon className="w-[18px] h-[18px] text-primary-indigo" strokeWidth={1.5} data-id="element-5477" data-mp="37wcgjh3sdbxq" />
              </span>
              <h3 className="text-[14px] font-bold text-dark-navy mt-3.5" data-id="element-5478" data-mp="3ctm40r7m7dix">
                {p.title}
              </h3>
              <p className="text-[13px] text-slate-500 leading-relaxed mt-1.5" data-id="element-5479" data-mp="s5hlo9csdkqq">
                {p.detail}
              </p>
            </div>)}
        </div>

        <p className="text-[12.5px] text-slate-400 mt-7" data-id="element-5480" data-mp="2cpv1h4txw264">
          Free to start · No website needed · Set up in an afternoon
        </p>
      </div>
    </motion.section>;
}